import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { useDeleteExerciseProgressMutation } from 'academy-common-lib/__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { useStartMutation, useSubmitMutation } from '../../../__generated__/chirp/com/hubspot/academy/assessments/rpc/ExerciseSubmissionService.hooks';
import { CREATE_PORTAL_MUTATION } from '../../../__generated__/chirp/com/hubspot/academy/simulation/environment/rpc/PortalService.dfc';
import { useGetPortalProvisioningStatusLazyQuery } from '../../../__generated__/chirp/com/hubspot/academy/simulation/environment/rpc/PortalService.hooks';
import { useGetRequirementsQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/ExerciseChirpService.hooks';
import { useHasGate } from '../accounts/accountsQueries.legacy';
import { deleteContentProgressFromCache, setContentProgressStatusCache } from '../userProgress/userProgressQueries';
import { Metrics } from '../../Metrics';
const DELETE_EXERCISE_PROGRESS_TIMEOUT_MS = 25000;
export const usePracticalExerciseRequirements = variables => {
  const {
    data: practicalExerciseRequirements,
    loading: practicalExerciseRequirementsLoading,
    error: practicalExerciseRequirementsError
  } = useGetRequirementsQuery({
    variables
  });
  return {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  };
};

/*
 * @move-to-academy-content-ui
 */
export const useSetPracticalExerciseStarted = () => {
  const [mutate, {
    loading: startPracticalExerciseLoading,
    called: startPracticalExerciseCalled,
    error: startPracticalExerciseError
  }] = useStartMutation();
  const startExercise = useCallback((lessonId, contentId, trackId) => {
    return mutate({
      variables: {
        contentId,
        lessonId,
        trackId
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'STARTED'
        });
      }
    }).then(result => result.data);
  }, [mutate]);
  return [startExercise, {
    startPracticalExerciseLoading,
    startPracticalExerciseCalled,
    startPracticalExerciseError
  }];
};
const getExerciseProgressChirpErrorType = error => {
  var _networkError$cause;
  const networkError = error === null || error === void 0 ? void 0 : error.networkError;
  return networkError === null || networkError === void 0 || (_networkError$cause = networkError.cause) === null || _networkError$cause === void 0 ? void 0 : _networkError$cause.errorType;
};

/*
 * @move-to-academy-content-ui
 */
export const useDeletePracticalExerciseProgress = () => {
  const [mutate, {
    loading: deletePracticalExerciseProgressLoading,
    called: deletePracticalExerciseProgressCalled,
    error: deletePracticalExerciseProgressError
  }] = useDeleteExerciseProgressMutation();
  const deleteExerciseProgress = useCallback(({
    contentId,
    lessonId,
    trackId
  }) => {
    const clearProgressFromCache = () => deleteContentProgressFromCache({
      contentId,
      lessonId,
      trackId
    });
    return mutate({
      variables: {
        contentId
      },
      context: {
        timeout: DELETE_EXERCISE_PROGRESS_TIMEOUT_MS
      },
      update() {
        clearProgressFromCache();
      }
    }).then(response => response.data).catch(mutationError => {
      const errorType = getExerciseProgressChirpErrorType(mutationError);
      // CHIRP returns CONTENT_NOT_FOUND / PROGRESS_NOT_DELETABLE when progress
      // is already gone; treat as success and clear cache so restart/retake can proceed.
      if (errorType === 'CONTENT_NOT_FOUND' || errorType === 'PROGRESS_NOT_DELETABLE') {
        Metrics.counter('exercise-mutation-call-error', {
          action: 'practical-exercise-delete-progress-already-gone',
          errorType
        }).increment();
        clearProgressFromCache();
        return undefined;
      }
      throw mutationError;
    });
  }, [mutate]);
  return [deleteExerciseProgress, {
    deletePracticalExerciseProgressLoading,
    deletePracticalExerciseProgressCalled,
    deletePracticalExerciseProgressError
  }];
};

/*
 * @move-to-academy-content-ui
 */
export const useSetManuallyGradedPracticalExerciseWithAssetSubmitted = () => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseWithAssetLoading,
    called: submitManuallyGradedPracticalExerciseWithAssetCalled,
    error: submitManuallyGradedPracticalExerciseWithAssetError
  }] = useSubmitMutation();
  const submitPracticalExerciseWithAsset = useCallback(({
    contentId,
    assetType,
    reference,
    trackId,
    lessonId
  }) => {
    return mutate({
      variables: {
        contentId,
        trackId,
        lessonId,
        exerciseSubmissionType: 'MANUAL',
        exerciseSubmissionAsset: {
          assetType,
          reference
        }
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'IN_REVIEW'
        });
      }
    }).then(response => response.data);
  }, [mutate]);
  return [submitPracticalExerciseWithAsset, {
    submitManuallyGradedPracticalExerciseWithAssetLoading,
    submitManuallyGradedPracticalExerciseWithAssetCalled,
    submitManuallyGradedPracticalExerciseWithAssetError
  }];
};
const PROVISIONING_POLL_INTERVAL_MS = 2000;
const PROVISIONING_MAX_ATTEMPTS = 150;
export const usePollPortalProvisioningStatus = () => {
  const [isPolling, setIsPolling] = useState(false);
  const pollCountRef = useRef(0);
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);
  const [startPollingQuery, {
    data: provisioningData,
    error: provisioningError,
    loading: provisioningLoading,
    stopPolling
  }] = useGetPortalProvisioningStatusLazyQuery({
    pollInterval: PROVISIONING_POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });
  useEffect(() => {
    if (!isPolling || provisioningLoading) return;
    const status = provisioningData === null || provisioningData === void 0 ? void 0 : provisioningData.provisioningStatus;
    if (status === 'COMPLETED') {
      var _resolveRef$current;
      stopPolling();
      setIsPolling(false);
      (_resolveRef$current = resolveRef.current) === null || _resolveRef$current === void 0 || _resolveRef$current.call(resolveRef);
      resolveRef.current = null;
      rejectRef.current = null;
      return;
    }
    if (status === 'FAILED' || provisioningError) {
      var _rejectRef$current;
      stopPolling();
      setIsPolling(false);
      (_rejectRef$current = rejectRef.current) === null || _rejectRef$current === void 0 || _rejectRef$current.call(rejectRef, new Error(provisioningError ? 'provisioning-query-error' : 'provisioning-failed'));
      resolveRef.current = null;
      rejectRef.current = null;
      return;
    }
    if (provisioningData !== undefined) {
      pollCountRef.current += 1;
    }
    if (pollCountRef.current >= PROVISIONING_MAX_ATTEMPTS) {
      var _rejectRef$current2;
      stopPolling();
      setIsPolling(false);
      (_rejectRef$current2 = rejectRef.current) === null || _rejectRef$current2 === void 0 || _rejectRef$current2.call(rejectRef, new Error('provisioning-timeout'));
      resolveRef.current = null;
      rejectRef.current = null;
    }
  }, [provisioningData, provisioningError, provisioningLoading, isPolling, stopPolling]);
  useEffect(() => {
    return () => {
      var _rejectRef$current3;
      stopPolling();
      (_rejectRef$current3 = rejectRef.current) === null || _rejectRef$current3 === void 0 || _rejectRef$current3.call(rejectRef, new Error('polling-aborted'));
      resolveRef.current = null;
      rejectRef.current = null;
    };
  }, [stopPolling]);
  const pollUntilProvisioned = useCallback(exerciseId => new Promise((resolve, reject) => {
    const startTime = Date.now();
    pollCountRef.current = 0;
    resolveRef.current = () => {
      Metrics.timer('exercise-portal-provisioning-duration').update(Date.now() - startTime);
      resolve();
    };
    rejectRef.current = reject;
    setIsPolling(true);
    void startPollingQuery({
      variables: {
        exerciseId
      }
    });
  }), [startPollingQuery]);
  return {
    pollUntilProvisioned
  };
};
export const usePracticalExerciseSimulationPortal = ({
  variables
}) => {
  const [mutate, {
    data: simulationPortal,
    loading: simulationPortalLoading,
    error: simulationPortalError
  }] = useMutation(CREATE_PORTAL_MUTATION, {
    variables
  });
  return {
    getOrCreateSimulationPortal: mutate,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  };
};

// Track IDs that are fully enabled (no gate)
// Sales Hub Cert, Marketing Hub Cert, Hubspot Reporting, Email Marketing, Set up Hubspot CRM for Growth, Service Hub Software, Email Marketing Software, Hubspot Content Hub for Marketers, Using Hubspot's Free Marketing Tools
const ENABLED_SIMULATION_TRACKS = {
  qa: [35515440, 65995930, 1097271, 1097331, 88515713, 94508094],
  prod: [9147486, 9219692, 9181275, 9151241, 8984877, 9148739, 9219097, 9206068, 9023279]
};

// Exercise IDs that are fully enabled (no gate)
// [Advance a Deal on Your Deal Board in HubSpot, Create a Task From a Contact Record, Book a Meeting Using the Meetings Tool]
const ENABLED_SIMULATION_EXERCISES = {
  qa: [22745667, 22745653, 22745655, 103330727],
  prod: [8251321, 14672225, 8251397]
};

// Exercise IDs that should NOT open in simulation mode (blacklist)
// 8171535: Make a call, Make a call in Hubspot
// AEO Certificate Without Exam practical exercises
const DISABLED_SIMULATION_EXERCISES = {
  qa: [],
  prod: [8171535, 14756457, 20830070, 20832438, 20830073, 20866573, 20859291, 20866478, 20859301, 20859299, 20866485, 20866487, 20866488, 20859394, 20859315, 20859320, 20866569, 20859402, 20859408, 20866593, 20866598, 20866600, 20859420, 20859427, 20859429, 20859434]
};
export const useIsSimulationExercise = (exerciseId, trackId) => {
  const environment = enviro.isQa() ? 'qa' : 'prod';
  const isDisabledSimulationExercise = DISABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const isEnabledSimulationTrack = trackId ? ENABLED_SIMULATION_TRACKS[environment].includes(trackId) : false;
  const isEnabledSimulationExercise = ENABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const {
    hasGate: hasAllExercisesGate,
    hasGateLoading: isAllExercisesGateLoading
  } = useHasGate('Academy:Simulation:AllExercises');
  const isSimulationExercise = useMemo(() => {
    if (isDisabledSimulationExercise) return false;
    if (isEnabledSimulationTrack) return true;
    if (isEnabledSimulationExercise) return true;
    if (hasAllExercisesGate) return true;
    return false;
  }, [isDisabledSimulationExercise, isEnabledSimulationTrack, isEnabledSimulationExercise, hasAllExercisesGate]);
  const isSimulationGateLoading = isEnabledSimulationTrack || isEnabledSimulationExercise ? false : isAllExercisesGateLoading;
  return {
    isSimulationExercise,
    isSimulationGateLoading
  };
};
export const usePracticalExercisePortalId = (exerciseId, trackId) => {
  const currentPortalId = PortalIdParser.getPortalIdFromPath();
  const {
    isSimulationExercise,
    isSimulationGateLoading
  } = useIsSimulationExercise(exerciseId, trackId);
  const {
    getOrCreateSimulationPortal,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  } = usePracticalExerciseSimulationPortal({
    variables: {
      exerciseId
    }
  });
  const {
    pollUntilProvisioned
  } = usePollPortalProvisioningStatus();
  const getOrCreatePortal = useCallback(() => {
    if (isSimulationExercise) {
      return getOrCreateSimulationPortal();
    }
    return undefined;
  }, [isSimulationExercise, getOrCreateSimulationPortal]);
  return {
    getOrCreatePortal,
    pollUntilProvisioned,
    portalId: isSimulationExercise ? (simulationPortal === null || simulationPortal === void 0 ? void 0 : simulationPortal.portalId) || currentPortalId : currentPortalId,
    portalIdLoading: simulationPortalLoading,
    portalIdError: simulationPortalError,
    isSimulationExercise,
    isSimulationGateLoading
  };
};